
/**
 * Rufus Q&A 组件 - 显示Amazon Rufus AI问答
 */

import React from 'react';
import { Card, Button, Space, Typography, Empty, Spin, Alert, Popconfirm } from 'antd';
import {
    ReloadOutlined, RobotOutlined, DeleteOutlined,
    ExperimentOutlined, TagsOutlined
} from '@ant-design/icons';

import { useRufusQA } from './hooks/useRufusQA';
import { QATable } from './components/QATable';
import { ReportView } from './components/ReportView';
import type { RufusQATabProps } from './types';

const { Text, Title } = Typography;

export const RufusQATab: React.FC<RufusQATabProps> = ({
    skuId,
    asin = '',
    site = 'US'
}) => {
    const {
        loading,
        analyzing,
        categorizing,
        qaList,
        analysisResult,
        loadQAs,
        handleCategorize,
        handleAnalyze,
        handleDelete,
        handleClear
    } = useRufusQA(skuId, asin);

    return (
        <div>
            {/* 头部操作区 */}
            <Card size="small" style={{ marginBottom: 16 }} variant="outlined">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                        <RobotOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                        <div>
                            <Title level={5} style={{ margin: 0 }}>Amazon Rufus AI 问答</Title>
                            <Text type="secondary">
                                从浏览器插件同步 Rufus 问答，了解客户关心的问题
                            </Text>
                        </div>
                    </Space>
                    <Space>
                        <Button
                            icon={<ExperimentOutlined />}
                            onClick={handleAnalyze}
                            loading={analyzing}
                            disabled={qaList.length === 0}
                            style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}
                        >
                            AI 深度洞察
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadQAs}
                            loading={loading}
                        >
                            刷新
                        </Button>
                        <Button
                            icon={<TagsOutlined />}
                            onClick={handleCategorize}
                            loading={categorizing}
                            disabled={qaList.length === 0}
                        >
                            AI 自动分类
                        </Button>
                        {qaList.length > 0 && (
                            <Popconfirm
                                title="确定清空所有问答记录吗？"
                                onConfirm={handleClear}
                            >
                                <Button icon={<DeleteOutlined />} danger>
                                    清空全部
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                </div>

                <ReportView report={analysisResult} />
            </Card >

            {/* ASIN 提示 */}
            {
                !asin && (
                    <Alert
                        message="请先在产品信息中填写ASIN"
                        description="需要关联ASIN以同步插件抓取的数据"
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )
            }

            {/* 问答列表 */}
            {
                loading && qaList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 48 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>加载中...</div>
                    </div>
                ) : qaList.length === 0 ? (
                    <Card variant="outlined">
                        <Empty
                            description="暂无Rufus问答数据"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                            <div style={{ color: '#888', marginTop: 8 }}>
                                请使用浏览器插件在亚马逊页面抓取并同步数据
                            </div>
                        </Empty>
                    </Card>
                ) : (
                    <Card title={`共 ${qaList.length} 条问答`} size="small" variant="outlined">
                        <QATable
                            data={qaList}
                            loading={loading}
                            onDelete={handleDelete}
                        />
                    </Card>
                )
            }
        </div >
    );
};

export default RufusQATab;
